import { useEffect, useRef } from 'react'
import { useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { NodeType } from '@ui/__generated__/graphql'
import { useSetupNodesEdges } from '@ui/lib/hooks/useSetupNodesEdges'
import { GetNodesAndEdgesLazyQuery, GetNodesAndEdgesWithSourcesLazyQuery } from '@ui/loaders/workflow'
import { PipelineWorkflow } from '@ui/pages/pipelinesWorkflow/PipelineWorkflow'
import { FeatureFlagName, useFeatureFlag } from '@ui/providers/FeatureFlagProvider'
import { useNodeAndEdgeStore, usePipelinesStore } from '@ui/stores'

import { PipelineWorkflowV2 } from './PipelineWorkflowV2'

export const ExistingPipeline = () => {
  const workflowUpdateEnabled = useFeatureFlag(FeatureFlagName.WorkflowUpdate)
  const loading = useRef(true)

  const { pipelineId } = useParams<{ pipelineId: string }>()

  const { isSettingUpPipeline, setupPipelineWorkflow, pipeline } = useSetupNodesEdges({
    isNew: false,
    id: pipelineId,
    withSteps: workflowUpdateEnabled,
  })

  const [getPipelineWorkflow] = workflowUpdateEnabled
    ? GetNodesAndEdgesWithSourcesLazyQuery()
    : GetNodesAndEdgesLazyQuery()

  const { updatePipeline, setUpdatePipeline, setRefreshLayout, setCurrentNodeId } = useNodeAndEdgeStore(
    useShallow((state) => ({
      updatePipeline: state.updatePipeline,
      setUpdatePipeline: state.setUpdatePipeline,
      setRefreshLayout: state.setRefreshLayout,
      setCurrentNodeId: state.setCurrentNodeId,
    }))
  )

  const { selectPipeline } = usePipelinesStore(
    useShallow((state) => ({
      selectPipeline: state.selectPipeline,
    }))
  )

  // Select the pipeline
  useEffect(() => {
    if (pipeline) {
      selectPipeline(pipeline)
      const firehoseNode = pipeline.nodes?.find((node) => node?.type === NodeType.Firehose)
      setCurrentNodeId(firehoseNode?.id || null)
      loading.current = false
    }

    return () => {
      selectPipeline(null)
    }
  }, [selectPipeline, pipeline])

  // Update pipeline when the pipeline update state is triggered
  useEffect(() => {
    if (!isSettingUpPipeline && updatePipeline) {
      getPipelineWorkflow({
        variables: { pipelineId: pipelineId as string },
        onCompleted: (data) => {
          const updatedPipeline = data.pipelineWorkflow

          if (updatedPipeline && updatedPipeline.id) {
            setupPipelineWorkflow(updatedPipeline)
            setRefreshLayout(true)

            // setCurrentNodeId(updatedPipeline.nodes?.find((node) => node?.type === NodeType.Firehose)?.id || null)
          }
        },
        onError: (error) => {
          console.error('Error executing query:', error.message)
        },
      })
    }
  }, [
    getPipelineWorkflow,
    isSettingUpPipeline,
    pipelineId,
    setCurrentNodeId,
    setRefreshLayout,
    setUpdatePipeline,
    setupPipelineWorkflow,
    updatePipeline,
  ])

  // if (isSettingUpPipeline || loading.current) {
  //   return <div className="flex h-full items-center justify-center">Loading...</div>
  // }

  return workflowUpdateEnabled ? <PipelineWorkflowV2 /> : <PipelineWorkflow />
}
